import clsx from 'clsx';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import Select, { FormatOptionLabelMeta } from 'react-select';
import { emptyFragment } from '../../EmptyFragment';
import { SearchParameter, SearchParameters } from '../../ListFilter/Types';
import { T, TPlainTextKey } from '../../Translations';
import Label from '../Form/Label';
import { StringOption } from '../Options/Types';
import { fetchValue, handleChange } from './ValueAndOnChange';
import { CustomStylesConfig, createStylesConfig } from './styles';

interface Props {
  disabled?: boolean;
  options: StringOption[];
  labelText?: TPlainTextKey;
  // Shown in the closed dropdown while the first ("All") option is selected,
  // so a row of filters reads by name instead of as several "All"s.
  placeholder?: TPlainTextKey;
  width?: string;
  parameters: SearchParameters;
  updatingParameters: (searchParameter: SearchParameter) => void;
  sortFilterKey: string;
}

function generateIdFromLabel(labelText: TPlainTextKey) {
  return `filterSelect-${labelText.toLowerCase().split(' ').join('-')}`;
}

function generateIdFromKey(sortFilterKey: string) {
  return `filterSelect-${sortFilterKey.toLowerCase().split('_').join('-')}`;
}

function GenericSortFilter({
  disabled,
  options,
  labelText,
  placeholder,
  width,
  parameters,
  updatingParameters,
  sortFilterKey,
}: Props) {
  const stylesConfig: CustomStylesConfig<StringOption> = createStylesConfig<StringOption>();
  const maybeLabel = fromNullable(labelText);
  const id = maybeLabel
    .map((t) => generateIdFromLabel(t))
    .getOrElseValue(generateIdFromKey(sortFilterKey));
  const placeholderLabel =
    (kind: TPlainTextKey) => (option: StringOption, meta: FormatOptionLabelMeta<StringOption>) =>
      meta.context === 'value' && option.value === options[0]?.value ? (
        <T kind={kind} />
      ) : (
        <>{option.label}</>
      );

  return (
    <div data-intercom-target={`${sortFilterKey.replace(/_/g, '-')}-filter-dropdown`}>
      {maybeLabel
        .map((t) => (
          <Label
            htmlFor={id}
            labelText={t}
            className="text-xs font-semibold whitespace-nowrap text-gray-800"
          />
        ))
        .getOrElse(emptyFragment)}
      <Select
        value={fetchValue(parameters, options, sortFilterKey)}
        onChange={handleChange(updatingParameters, sortFilterKey)}
        styles={stylesConfig}
        options={options}
        isDisabled={disabled}
        formatOptionLabel={fromNullable(placeholder)
          .map(placeholderLabel)
          .getOrElseValue((option: StringOption) => (
            <>{option.label}</>
          ))}
        name="filter"
        inputId={id}
        className={clsx(
          'mt-1 block rounded-md',
          'border-gray-300 bg-white text-sm font-medium',
          width,
        )}
      />
    </div>
  );
}

export default observer(GenericSortFilter);
